// RecommendationsSection.jsx
// Component for displaying recommended listings - Enhanced with real recommendations

import React, { useState, useEffect } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';
import ListingCard from '../../components/ListingCard';

// Modern SVG Icons
const SparklesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z"></path>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"></polyline>
    <polyline points="17,6 23,6 23,12"></polyline>
  </svg>
);

const useRecommendations = (currentListing) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!currentListing) return;

      try {
        // Fetch similar items based on category (no status filter in query)
        const similarQuery = query(
          collection(db, 'listings'),
          where('category', '==', currentListing.category),
          limit(10) // Get more to filter out unwanted ones
        );

        const similarSnapshot = await getDocs(similarQuery);
        const similarItems = similarSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(item => 
            item.id !== currentListing.id && 
            item.status !== "sold" && 
            item.status !== "archived" && 
            item.status !== "deep_archived"
          )
          .slice(0, 3);

        // Fetch recent items from other categories (no status filter in query)
        const recentQuery = query(
          collection(db, 'listings'),
          limit(10) // Get more to filter out unwanted ones
        );

        const recentSnapshot = await getDocs(recentQuery);
        const recentItems = recentSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(item => 
            item.id !== currentListing.id && 
            item.category !== currentListing.category &&
            item.status !== "sold" && 
            item.status !== "archived" && 
            item.status !== "deep_archived"
          )
          .slice(0, 3);

        setRecommendations({
          similar: similarItems,
          recent: recentItems
        });
      } catch (error) {
        setRecommendations({ similar: [], recent: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [currentListing]);

  return { recommendations, loading };
};

const RecommendationsSection = ({ currentListing }) => {
  const { recommendations, loading } = useRecommendations(currentListing);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div style={modernStyles.loadingContainer}>
        <div style={modernStyles.spinner}></div>
        <p style={modernStyles.loadingText}>Loading recommendations...</p>
      </div>
    );
  }

  const hasSimilar = recommendations.similar && recommendations.similar.length > 0;
  const hasRecent = recommendations.recent && recommendations.recent.length > 0;

  if (!hasSimilar && !hasRecent) {
    return (
      <div style={modernStyles.container}>
        <div style={modernStyles.emptyState}>
          <SparklesIcon />
          <h3 style={modernStyles.emptyTitle}>No recommendations available</h3>
          <p style={modernStyles.emptyDescription}>
            Check back later for similar items and trending listings!
          </p>
          <button 
            style={modernStyles.browseButton}
            onClick={() => navigate('/market')}
          >
            Browse All Items
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={modernStyles.container}>
      {/* Similar Items Section */}
      {hasSimilar && (
        <div style={modernStyles.section}>
          <div style={modernStyles.sectionHeader}>
            <div style={modernStyles.sectionIcon}>
              <SparklesIcon />
            </div>
            <div>
              <h2 style={modernStyles.sectionTitle}>Similar Items</h2>
              <p style={modernStyles.sectionSubtitle}>
                More {currentListing.category.toLowerCase()} items you might like
              </p>
            </div>
          </div>
          
          <div style={modernStyles.grid}>
            {recommendations.similar.map((listing) => (
              <div key={listing.id} style={modernStyles.cardWrapper}>
                <ListingCard listing={listing} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent/Trending Items Section */}
      {hasRecent && (
        <div style={modernStyles.section}>
          <div style={modernStyles.sectionHeader}>
            <div style={modernStyles.sectionIcon}>
              <TrendingUpIcon />
            </div>
            <div>
              <h2 style={modernStyles.sectionTitle}>Trending Now</h2>
              <p style={modernStyles.sectionSubtitle}>
                Popular items from other categories
              </p>
            </div>
          </div>
          
          <div style={modernStyles.grid}>
            {recommendations.recent.map((listing) => (
              <div key={listing.id} style={modernStyles.cardWrapper}>
                <ListingCard listing={listing} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Browse All CTA */}
      <div style={modernStyles.ctaSection}>
        <div style={modernStyles.ctaContent}>
          <h3 style={modernStyles.ctaTitle}>Want to see more?</h3>
          <p style={modernStyles.ctaDescription}>
            Discover hundreds of items from ISU students
          </p>
          <button 
            style={modernStyles.ctaButton}
            onClick={() => navigate('/market')}
          >
            Browse All Items
          </button>
        </div>
      </div>
    </div>
  );
};

// Modern styles
const modernStyles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: `${LAYOUT.spacing.xxxl} ${LAYOUT.spacing.lg}`,
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xxxl,
  },

  // Loading State
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: LAYOUT.spacing.xxxl,
    gap: LAYOUT.spacing.lg,
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid ${COLORS.border}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: COLORS.text.secondary,
    fontSize: '1rem',
  },

  // Section Styles
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xl,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.lg,
  },
  sectionIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
  },
  sectionTitle: {
    ...TYPOGRAPHY.heading2,
    fontSize: '1.875rem',
    marginBottom: LAYOUT.spacing.xs,
  },
  sectionSubtitle: {
    color: COLORS.text.secondary,
    fontSize: '1rem',
    margin: 0,
  },

  // Grid Layout
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: LAYOUT.spacing.xl,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: LAYOUT.spacing.lg,
    },
  },
  cardWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },

  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: LAYOUT.spacing.xxxl,
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${COLORS.border}`,
  },
  emptyTitle: {
    ...TYPOGRAPHY.heading3,
    marginBottom: LAYOUT.spacing.md,
    color: COLORS.text.primary,
  },
  emptyDescription: {
    color: COLORS.text.secondary,
    fontSize: '1rem',
    marginBottom: LAYOUT.spacing.xl,
  },
  browseButton: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // CTA Section
  ctaSection: {
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xxxl,
    textAlign: 'center',
    border: `1px solid ${COLORS.border}`,
  },
  ctaContent: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  ctaTitle: {
    ...TYPOGRAPHY.heading3,
    marginBottom: LAYOUT.spacing.md,
    color: COLORS.text.primary,
  },
  ctaDescription: {
    color: COLORS.text.secondary,
    fontSize: '1.125rem',
    marginBottom: LAYOUT.spacing.xl,
    lineHeight: 1.6,
  },
  ctaButton: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xxxl}`,
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 12px ${COLORS.shadow}`,
  },
};

// Add spinner animation
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('recommendations-spinner-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'recommendations-spinner-styles';
    style.textContent = spinnerStyles;
    document.head.appendChild(style);
  }
}

export default RecommendationsSection;